import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, MoveUpRight } from "lucide-react";
import { EASE, Lines } from "@/components/motion/Reveal";
import { Tiles, mosaicGradient } from "@/components/motion/Tiles";
import { FLEET } from "@/data/fleet";

const STATS = [
  { value: `${FLEET.reduce((a, v) => a + (v.units || 1), 0)}`, label: "Mezzi in flotta" },
  { value: "24 m", label: "Altezza massima" },
  { value: "Pat. B", label: "Per le autocarrate" },
  { value: "24 h", label: "Consegna in cantiere" },
];

const TiltCard = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rx.set(-py * 14);
    ry.set(px * 14);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d", perspective: 1000 }}
      initial={{ opacity: 0, y: 60, rotateY: -10 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
      className="relative w-full max-w-sm border border-white/15 bg-ink-2/70 p-6 backdrop-blur-xl"
      data-testid="hero-spec-card"
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow text-blue-500">In evidenza</p>
        <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulseDot" /> Disponibile
        </span>
      </div>
      <h3 className="display mt-4 text-3xl text-white">Snake 2413<br />su Iveco Daily</h3>
      <div className="mt-5 grid grid-cols-3 gap-3 border-t border-white/10 pt-5">
        {[["24 m", "Altezza"], ["13 m", "Sbraccio"], ["B", "Patente"]].map(([v, l]) => (
          <div key={l}>
            <p className="font-display text-2xl font-extrabold text-white">{v}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">{l}</p>
          </div>
        ))}
      </div>
      <Link to="/parco-mezzi/sollevamento/piattaforma-oil-steel-snake-2413-daily" className="group mt-6 flex items-center justify-between border-t border-white/10 pt-4 font-display text-sm font-bold uppercase tracking-wider text-white/80 hover:text-white" data-testid="hero-spec-card-link">
        Scheda tecnica
        <MoveUpRight className="h-4 w-4 text-blue-500 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </Link>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-ink text-white grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="/images/hero.jpg" alt="Piattaforma aerea DESP al lavoro con l'Etna sullo sfondo" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      <div className="absolute inset-0 blueprint opacity-40" />

      <div className="container-x relative flex min-h-[100svh] flex-col justify-end pb-10 pt-36 sm:pb-16">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <motion.div style={{ y: textY, opacity }} className="lg:col-span-8">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }} className="eyebrow flex items-center gap-3 text-blue-500">
              <Tiles animate={false} /> Catania · Sicilia orientale · Noleggio mezzi da cantiere
            </motion.p>
            <Lines
              inView={false}
              delay={0.45}
              lines={["Noleggio piattaforme", "aeree ed escavatori.", "Dove serve,", "quando serve."]}
              className="display mt-5 text-[2.75rem] leading-[0.92] sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
              lineClassName={(i) => (i >= 2 ? "text-blue-500" : "")}
            />
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8, ease: EASE }} className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              Piattaforme a forbice, autocarrate fino a 24 metri, miniescavatori, dumper e minipale. Consegnati direttamente nel tuo cantiere, pronti a lavorare.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8, ease: EASE }} className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/parco-mezzi" className="btn-primary" data-testid="hero-cta-explore">
                Esplora il parco mezzi <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contatti" className="btn-ghost" data-testid="hero-cta-quote">
                Preventivo rapido
              </Link>
            </motion.div>
          </motion.div>

          <div className="hidden lg:col-span-4 lg:flex lg:justify-end">
            <TiltCard />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-14 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          <div className="col-span-2 h-1 sm:col-span-4" style={{ backgroundImage: mosaicGradient(12) }} />
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 + i * 0.08, duration: 0.7, ease: EASE }}
              className="bg-ink/70 px-5 py-5 backdrop-blur"
            >
              <p className="font-display text-3xl font-extrabold text-white sm:text-4xl">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/45">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-6 right-6 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/40 lg:flex"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        Scorri <ArrowDown className="h-3.5 w-3.5" />
      </motion.div>
    </section>
  );
};
